import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePlayer } from '@/context/PlayerContext';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { TrackCard } from '@/components/TrackCard';
import { Button } from '@/components/ui/button';
import { 
  MusicNote, MapPin, Globe, SpotifyLogo, SoundcloudLogo, InstagramLogo,
  Play, Heart, ArrowLeft
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MusicianProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const { playTrack } = usePlayer();
  const [musician, setMusician] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);

  useEffect(() => {
    fetchProfile();
    if (user?.user_type === 'organizer') {
      checkFavorite();
    }
  }, [id, user]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/discover/musicians/${id}`);
      setMusician(response.data.musician);
      setTracks(response.data.tracks);
      setAlbums(response.data.albums);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      const fav = response.data.find(f => f.id === id);
      if (fav) {
        setIsFavorite(true);
        setFavoriteId(fav.favorite_id);
      }
    } catch (error) {
      console.error('Failed to check favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Accedi per salvare nei preferiti');
      return;
    }
    if (user.user_type !== 'organizer') {
      toast.error('Solo gli organizzatori possono salvare preferiti');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${favoriteId}`);
        setIsFavorite(false);
        setFavoriteId(null);
        toast.success('Rimosso dai preferiti');
      } else {
        const response = await axios.post(`${API}/favorites`, { musician_id: id });
        setIsFavorite(true);
        // Fetch the new favorite ID
        const favRes = await axios.get(`${API}/favorites`);
        const fav = favRes.data.find(f => f.id === id);
        if (fav) setFavoriteId(fav.favorite_id);
        toast.success('Aggiunto ai preferiti');
      }
    } catch (error) {
      toast.error('Errore durante l\'operazione');
    }
  };

  const playAllTracks = () => {
    if (tracks.length > 0) {
      const playableTracks = tracks.filter(t => t.audio_path || t.external_url);
      if (playableTracks.length > 0) {
        playTrack(playableTracks[0], playableTracks);
      }
    }
  };

  const getProfileImage = () => {
    if (musician?.profile_image) {
      return `${API}/files/${musician.profile_image}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!musician) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 text-center">
          <p className="text-[#A1A1AA]">Artista non trovato</p>
          <Link to="/discover">
            <Button className="mt-4">Torna alla scoperta</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Hero */}
      <div className="relative">
        <div className="h-[50vh] relative">
          {/* Background */}
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/6270155/pexels-photo-6270155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Band performance"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
          </div>

          {/* Back Button */}
          <div className="absolute top-20 left-8 z-10">
            <Link to="/discover" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors">
              <ArrowLeft size={20} />
              Indietro
            </Link>
          </div>
        </div>

        {/* Profile Info */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-48 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row gap-8 items-start"
          >
            {/* Profile Image */}
            <div className="w-48 h-48 rounded-2xl overflow-hidden bg-[#1A1A1A] flex-shrink-0 border-4 border-[#050505]">
              {getProfileImage() ? (
                <img src={getProfileImage()} alt={musician.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <MusicNote size={64} className="text-[#333]" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {musician.genres && musician.genres.length > 0 ? (
                  musician.genres.map(genre => (
                    <span key={genre} className="badge badge-primary">{genre}</span>
                  ))
                ) : musician.genre && (
                  <span className="badge badge-primary">{musician.genre}</span>
                )}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-4">{musician.name}</h1>
              
              <div className="flex items-center gap-6 text-[#A1A1AA] mb-6">
                {musician.location && (
                  <span className="flex items-center gap-2">
                    <MapPin size={18} />
                    {musician.location}
                  </span>
                )}
                <span>{tracks.length} {tracks.length === 1 ? 'brano' : 'brani'}</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-4">
                {tracks.length > 0 && (
                  <Button 
                    onClick={playAllTracks}
                    className="bg-[#CCFF00] text-black hover:bg-[#E5FF00] rounded-full px-8"
                    data-testid="play-all-btn"
                  >
                    <Play size={20} weight="fill" className="mr-2" />
                    Riproduci tutto
                  </Button>
                )}
                {user?.user_type === 'organizer' && (
                  <Button
                    onClick={toggleFavorite}
                    variant="outline"
                    className="border-white/20 rounded-full"
                    data-testid="favorite-btn"
                  >
                    {isFavorite ? (
                      <Heart size={20} weight="fill" className="text-[#FF0055] mr-2" />
                    ) : (
                      <Heart size={20} className="mr-2" />
                    )}
                    {isFavorite ? 'Nei preferiti' : 'Salva'}
                  </Button>
                )}
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-4 mt-6">
                {musician.website && (
                  <a href={musician.website} target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-white transition-colors">
                    <Globe size={24} />
                  </a>
                )}
                {musician.spotify_url && (
                  <a href={musician.spotify_url} target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#1DB954] transition-colors">
                    <SpotifyLogo size={24} weight="fill" />
                  </a>
                )}
                {musician.soundcloud_url && (
                  <a href={musician.soundcloud_url} target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#FF5500] transition-colors">
                    <SoundcloudLogo size={24} weight="fill" />
                  </a>
                )}
                {musician.instagram_url && (
                  <a href={musician.instagram_url} target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#E1306C] transition-colors">
                    <InstagramLogo size={24} weight="fill" />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-32">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Bio */}
          <div className="lg:col-span-1">
            {musician.bio && (
              <div className="card rounded-xl p-6 mb-6">
                <h2 className="text-lg font-semibold text-white mb-4">Bio</h2>
                <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-line">{musician.bio}</p>
              </div>
            )}
          </div>

          {/* Tracks */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-white mb-6">Brani</h2>
            {tracks.length > 0 ? (
              <div className="card rounded-xl p-4">
                <div className="space-y-1">
                  {tracks.map((track, index) => (
                    <TrackCard 
                      key={track.id} 
                      track={track} 
                      index={index}
                      onPlay={() => playTrack(track, tracks)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="card rounded-xl p-12 text-center">
                <MusicNote size={48} className="text-[#333] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Nessun brano disponibile</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
